import { useEffect, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import { apiError } from "../api";
import { useAuth } from "../AuthContext";
import { PLATFORM_META, PlatformBadge, inr } from "../platforms";
import { Download, FileSpreadsheet } from "lucide-react";

const inputCls = "border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-600";

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState([]);
  const [execRows, setExecRows] = useState([]);
  const [busy, setBusy] = useState("");

  const load = () => {
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/reports/summary", { params }).then((r) => setRows(r.data));
    if (isAdmin) api.get("/reports/executives", { params }).then((r) => setExecRows(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const download = async (report) => {
    setBusy(report);
    try {
      const params = { report };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/reports/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = report === "summary" ? "platform_summary.csv" : "invoice_register.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const downloadTally = async () => {
    setBusy("tally");
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/reports/tally-export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tally_stock_transfer_export.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const totals = rows.reduce((a, r) => ({
    count: a.count + r.count, qty: a.qty + r.qty, taxable: a.taxable + r.taxable,
    tax: a.tax + r.tax, grand_total: a.grand_total + r.grand_total,
  }), { count: 0, qty: 0, taxable: 0, tax: 0, grand_total: 0 });

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Analytics</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>Reports</h1>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className={labelCls}>From</label>
          <input data-testid="report-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={`${inputCls} block mt-1`} />
        </div>
        <div>
          <label className={labelCls}>To</label>
          <input data-testid="report-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={`${inputCls} block mt-1`} />
        </div>
        <button data-testid="download-summary-csv-btn" onClick={() => download("summary")} disabled={!!busy}
          className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 disabled:opacity-60">
          <FileSpreadsheet size={15} /> Platform Summary CSV
        </button>
        <button data-testid="download-register-csv-btn" onClick={() => download("register")} disabled={!!busy}
          className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-500 text-slate-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 disabled:opacity-60">
          <Download size={15} /> Invoice Register CSV
        </button>
        <button data-testid="download-tally-xlsx-btn" onClick={downloadTally} disabled={!!busy}
          className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 disabled:opacity-60">
          <FileSpreadsheet size={15} /> Tally Export (.xlsx)
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead>
            <tr className="bg-[#0A192F] text-white text-left">
              {["Platform", "Invoices", "E-Invoiced", "Total Qty", "Taxable Value", "Tax", "Grand Total"].map((h) => (
                <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="report-summary-body" className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.platform} className="hover:bg-slate-50 transition-colors duration-200">
                <td className="px-4 py-3"><span className="inline-flex items-center gap-2 font-medium"><PlatformBadge platform={r.platform} size={13} />{PLATFORM_META[r.platform]?.name}</span></td>
                <td className="px-4 py-3 text-right font-mono">{r.count}</td>
                <td className="px-4 py-3 text-right font-mono">{r.einvoiced}</td>
                <td className="px-4 py-3 text-right font-mono">{r.qty}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(r.taxable)}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(r.tax)}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold">{inr(r.grand_total)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-slate-400">No invoices in this period.</td></tr>}
            {rows.length > 0 && (
              <tr className="bg-slate-50 font-semibold">
                <td className="px-4 py-3">Total</td>
                <td className="px-4 py-3 text-right font-mono">{totals.count}</td>
                <td className="px-4 py-3" />
                <td className="px-4 py-3 text-right font-mono">{totals.qty}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(totals.taxable)}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(totals.tax)}</td>
                <td className="px-4 py-3 text-right font-mono">{inr(totals.grand_total)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isAdmin && (
        <div>
          <h2 className="font-semibold text-slate-900 mb-3">Executive-wise Sales</h2>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="bg-[#0A192F] text-white text-left">
                  {["Executive", "Invoices", "Total Qty", "Taxable Value", "Tax", "Grand Total"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="report-executives-body" className="divide-y divide-slate-100">
                {execRows.map((r) => (
                  <tr key={r.executive} className="hover:bg-slate-50 transition-colors duration-200">
                    <td className="px-4 py-3 font-medium">{r.executive}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.count}</td>
                    <td className="px-4 py-3 text-right font-mono">{r.qty}</td>
                    <td className="px-4 py-3 text-right font-mono">{inr(r.taxable)}</td>
                    <td className="px-4 py-3 text-right font-mono">{inr(r.tax)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{inr(r.grand_total)}</td>
                  </tr>
                ))}
                {execRows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-slate-400">No invoices in this period.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
